/**
 * Phantom's universal link, for a phone with no injected wallet.
 *
 * `PhantomWalletAdapter` already does this, but only where
 * `isIosAndRedirectable()` holds: an iOS user agent that also says Safari.
 * Everywhere else it polls for an extension that will never appear, leaves
 * the state at `NotDetected`, and the holder is told to install one — which
 * on Android Chrome is not a thing that exists, and in an iOS webview is not
 * a thing they can do from where they are.
 *
 * The link opens the current page inside Phantom's own browser, where the
 * wallet is injected and the ordinary desktop path runs.
 */
const BROWSE = "https://phantom.app/ul/browse";

/** Phones only. A desktop misread here would navigate someone away for nothing. */
export function isPhone(userAgent: string): boolean {
  return /android|iphone|ipad|ipod/i.test(userAgent);
}

/**
 * Where to send a phone that has no wallet injected, or null when the page is
 * somewhere that could have an extension instead.
 */
export function phantomBrowseUrl(
  location: { href: string; origin: string },
  userAgent: string
): string | null {
  if (!isPhone(userAgent)) return null;
  // Phantom reads the target from the path and the referrer from the query.
  return `${BROWSE}/${encodeURIComponent(location.href)}?ref=${encodeURIComponent(location.origin)}`;
}

// Brave itself, not only Brave Wallet holding `window.solana`: 33 of 36 Brave
// sessions told to install Phantom in a day (2026-10-19) never saw this.
export function braveWalletFirst(): boolean {
  const solana: unknown = Reflect.get(window, "solana");
  return (
    typeof Reflect.get(window.navigator, "brave") === "object" ||
    (typeof solana === "object" &&
      solana !== null &&
      Reflect.get(solana, "isBraveWallet") === true)
  );
}
